import {
    FORMS_INVITATION_ERROR,
    FORMS_INVITATION_MARK_NOTIFICATION_SHOWN,
    FORMS_INVITATION_SET_DATA,
    FORMS_INVITATION_SET_LOADING,
    FORMS_INVITATION_SET_RESPONSE_SET_ID,
    FORMS_INVITATION_SET_VIEW,
    FORMS_INVITATION_SUCCESS,
    FORMS_INVITATION_UPDATE_ANSWER,
    FormsInvitationAction
} from 'actions/formsInvitation/FormsInvitationActionsTypes';
import { fetchMarketplaceBySlugThunk } from 'actions/marketplace/MarketplaceActions';
import { push } from 'connected-react-router';
import { ApiPublicClient } from 'api/ApiClient';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import {
    CustomQuestionResponseDto,
    FormDto,
    FormResponseSetDto,
    FormResponseSetPayload,
    FormsInvitationResponse,
    PetDto,
    QuestionDto
} from './FormsInvitationDtos';
import { convertToSavePetDto } from 'actions/pets/PetsConverter';
import { PetDto as SavePetDtoType } from 'actions/pets/PetsDtos';
import { serializeToFormData } from 'api/serializer';
import { SavePet } from 'types/Pet';
import { fetchMasterDataThunk } from 'actions/masterData/MasterDataActions';

// URLs
const formResponseSetsUrl = 'form_response_sets';

const setLoading = (loading: boolean): FormsInvitationAction => ({
    type: FORMS_INVITATION_SET_LOADING,
    payload: { loading }
});

const setData = (data: FormsInvitationResponse): FormsInvitationAction => ({
    type: FORMS_INVITATION_SET_DATA,
    payload: { data }
});

const setView = (view: 'client' | 'pets' | 'success'): FormsInvitationAction => ({
    type: FORMS_INVITATION_SET_VIEW,
    payload: { view }
});

const errorHandler = (error: unknown): FormsInvitationAction => {
    console.error('FormsInvitation Error:', error);

    // Check if error is an Axios error with response
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { error?: string } } };
        const status = axiosError.response?.status;
        const message = axiosError.response?.data?.error;

        if (status) {
            return {
                type: FORMS_INVITATION_ERROR,
                payload: {
                    code: status,
                    message: message || 'An error occurred'
                }
            };
        }
    }

    return {
        type: FORMS_INVITATION_ERROR
    };
};

export const updateAnswerAction = (
    formId: number,
    questionId: number,
    answer: string | string[],
    subjectId?: number
): FormsInvitationAction => ({
    type: FORMS_INVITATION_UPDATE_ANSWER,
    payload: { formId, questionId, answer, subjectId }
});

const setResponseSetId = (
    formId: number,
    responseSetId: number,
    subjectId?: number
): FormsInvitationAction => ({
    type: FORMS_INVITATION_SET_RESPONSE_SET_ID,
    payload: { formId, responseSetId, subjectId }
});

export const markNotificationShownAction = (
    notificationType: 'addQuestion' | 'addPet'
): FormsInvitationAction => ({
    type: FORMS_INVITATION_MARK_NOTIFICATION_SHOWN,
    payload: { notificationType }
});

export const initFormsInvitationThunk = (
    slug: string,
    token: string
): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        try {
            dispatch(setLoading(true));

            // Fetch marketplace data
            const marketplace = await dispatch(fetchMarketplaceBySlugThunk(slug));
            // Fetch master data
            await dispatch(fetchMasterDataThunk(marketplace.id));

            // Fetch forms invitation data
            const { data } = await ApiPublicClient.get<FormsInvitationResponse>('get_forms', {
                params: {
                    token: token
                }
            });

            dispatch(setData(data));
            dispatch(setView('client'));
        } catch (error) {
            dispatch(errorHandler(error));

            // Check error status code
            if (error && typeof error === 'object' && 'response' in error) {
                const axiosError = error as { response?: { status?: number } };
                const status = axiosError.response?.status;

                // Redirect to home only for 404 errors
                if (status === 404) {
                    dispatch(push('/'));
                }
                // For 410 (expired), don't redirect - show LinkExpired component
            } else {
                // For other errors (network, etc), redirect to home
                dispatch(push('/'));
            }
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const continueToFormsInvitationPetsThunk = (): ThunkAction<
    void,
    RootState,
    null,
    RootAction
> => {
    return async dispatch => {
        dispatch(setView('pets'));
    };
};

export const backToClientFormsThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async dispatch => {
        dispatch(setView('client'));
    };
};

/**
 * Save form responses (create or update)
 * Siguiendo el patrón de saveCustomerThunk
 */
export const saveFormResponsesThunk = (
    formId: number,
    answers: { [questionId: number]: string | string[] },
    subjectType: 'Customer' | 'Pet',
    subjectId: number
): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            const { formsInvitation, marketplace } = getState();
            const data = formsInvitation.data;

            if (!data) {
                throw new Error('No invitation data found');
            }

            // Get the form to find questions
            let form;
            if (subjectType === 'Customer') {
                form = data.customer.forms.find((f: FormDto) => f.id === formId);
            } else {
                // Find in pets
                const pet = data.pets.find((p: PetDto) => p.id === subjectId);
                form = pet?.forms.find((f: FormDto) => f.id === formId);
            }

            if (!form) {
                throw new Error('Form not found');
            }

            // Convert answers to custom_question_responses_attributes
            const custom_question_responses_attributes: CustomQuestionResponseDto[] = [];

            form.questions.forEach((question: QuestionDto) => {
                const answer = answers[question.id];
                if (answer !== undefined && answer !== null && answer !== '') {
                    if (Array.isArray(answer) && answer.length === 0) {
                        return; // Skip empty arrays
                    }
                    if (typeof answer === 'string' && answer.trim() === '') {
                        return; // Skip empty strings
                    }

                    custom_question_responses_attributes.push({
                        id: question.response?.id,
                        custom_question_id: question.id,
                        question_snapshot: question.question_text,
                        answer: Array.isArray(answer) ? answer.join(', ') : answer
                    });
                }
            });

            if (custom_question_responses_attributes.length === 0) {
                return;
            }

            const payload: FormResponseSetPayload = {
                form_id: formId,
                marketplace_id: marketplace.data.id,
                customer_id: data.customer.id,
                form_invitation_id: data.invitation.id,
                subject_type: subjectType,
                subject_id: subjectId,
                custom_question_responses_attributes
            };

            const compositeKey = subjectType === 'Pet' ? `${formId}_${subjectId}` : String(formId);
            const formResponseMap = formsInvitation.formResponseSetIds as Record<string, number>;
            const existingResponseSetId = formResponseMap[compositeKey];

            if (existingResponseSetId) {
                // UPDATE - similar to ApiClient.patch
                await ApiPublicClient.patch<FormResponseSetDto>(
                    `${formResponseSetsUrl}/${existingResponseSetId}`,
                    { form_response_set: payload }
                );
            } else {
                // CREATE - similar to ApiClient.post
                const response = await ApiPublicClient.post<FormResponseSetDto>(
                    formResponseSetsUrl,
                    { form_response_set: payload }
                );

                // Save the response set ID
                dispatch(setResponseSetId(formId, response.data.id, subjectId));
            }

            // Update local answers (include subjectId for Pet answers)
            Object.entries(answers).forEach(([questionId, answer]) => {
                const subjId = subjectType === 'Pet' ? subjectId : undefined;
                dispatch(updateAnswerAction(formId, Number(questionId), answer, subjId));
            });
        } catch (error) {
            dispatch(errorHandler(error));
            console.error('Error saving form responses:', error);
            // Don't dispatch error to avoid disrupting user experience
            // The save will be retried on next change
        }
    };
};

export const submitFormsInvitationThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch(setLoading(true));

            const { formsInvitation } = getState();
            const token = formsInvitation.data?.invitation.token;

            if (!token) {
                throw new Error('No token found');
            }

            // Fetch forms invitation data
            await ApiPublicClient.get<FormsInvitationResponse>('form_invitations/invalidate', {
                params: {
                    token: token
                }
            });

            dispatch({
                type: FORMS_INVITATION_SUCCESS
            });

            dispatch(setView('success'));
        } catch (error) {
            dispatch(errorHandler(error));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

/**
 * Save pet for FormsInvitation flow
 */
export const saveFormsInvitationPetThunk = (
    pet: SavePet
): ThunkAction<Promise<void>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            const { formsInvitation, marketplace } = getState();
            const data = formsInvitation.data;

            if (!data) {
                throw new Error('No invitation data found');
            }

            const marketplaceId = marketplace.data.id;
            const customerId = data.customer.id;
            const token = data.customer.token;

            // Convert pet to DTO format
            const petDto = convertToSavePetDto(pet, marketplaceId);

            // Add customer ownership
            const petPayload = {
                ...petDto,
                owns_attributes: [{ customer_id: customerId }]
            };

            const formData = serializeToFormData({
                pet: petPayload
            });

            // Create new pet
            //const response = await ApiPublicClient.post<SavePetDtoType>('pets', formData);
            await ApiPublicClient.post<SavePetDtoType>('pets', formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            // Refresh forms invitation data to include the new pet
            const { data: refreshedData } = await ApiPublicClient.get<FormsInvitationResponse>(
                'get_forms',
                {
                    params: {
                        token: data.invitation.token
                    }
                }
            );

            dispatch(setData(refreshedData));
        } catch (error) {
            dispatch(errorHandler(error));
            throw error;
        }
    };
};
